// trackcoprv2/frontend/src/components/CRSScoreForm.js

import React, { useState, useCallback } from 'react';
// Import MUI components
import {
    Box,
    Button,
    Typography,
    CircularProgress, // Added for loading state
    Alert,           // Added for error display
    Collapse         // Added for smooth transitions
} from '@mui/material';
import axiosInstance from '../api/axiosInstance'; // Import axiosInstance

// Import the calculation function and data
import { calculateOfficialCRS } from '../utils/officialCrsCalculations';
// Import constants
import {
    BAD_VALUE,
    maritalStatusOptions,
    yesNoOptions,
    ageOptions,
    educationLevelOptions,
    canadianEducationLevelOptions,
    languageTestOptions,
    testValueToNameMap,
    languageScoreOptions,
    canadianWorkExperienceOptions,
    foreignWorkExperienceOptions,
    jobOfferNocOptions
} from '../constants/crsFormOptions';
// Import Hooks
import useCRSConditionals from '../hooks/useCRSConditionals';
// Import Utilities
import { mapFormDataToCalculationParams } from '../utils/crsUtils';
import { mapFormDataToLabels } from '../utils/crsLabels'; // Import the new mapping function
import { validateCRSForm } from '../utils/crsValidation';
// Import section components
import PersonalSection from './CRSFormSections/PersonalSection';
import EducationSection from './CRSFormSections/EducationSection';
import LanguageSection from './CRSFormSections/LanguageSection';
import WorkExperienceSection from './CRSFormSections/WorkExperienceSection';
import AdditionalPointsSection from './CRSFormSections/AdditionalPointsSection';
import SpouseFactorsSection from './CRSFormSections/SpouseFactorsSection';
// Removed ResultDisplay and CRSSuggestions imports - they are in the parent

import '../App.css'; // Keep App.css if needed for styling

// --- Component ---

function CRSScoreForm({
    onScoreCalculated,
    user,
    formData,
    handleChange: originalHandleChange,
    handleLanguageScoreChange: originalHandleLanguageScoreChange,
    handleFocus
}) {

    // --- Wrapped Handlers to Invalidate Score on Change ---

    const handleChange = (e) => {
        originalHandleChange(e);
        // When form data changes, the calculated score is no longer valid.
        // Resetting it to null forces the user to recalculate before saving.
        // This prevents saving a new profile with a stale score.
        setCalculatedScore(null);
        // Also reset the save status to clear any "Saved!" message or error.
        setSaveStatus({ saving: false, error: null, success: false });
    };

    const handleLanguageScoreChange = (test, field, value) => {
        originalHandleLanguageScoreChange(test, field, value);
        // Same logic as above for language score changes.
        setCalculatedScore(null);
        setSaveStatus({ saving: false, error: null, success: false });
    };
    const {
        showSpouseIsPRQuestion,
        showSpouseAccompanyQuestion,
        showSpouseFactors,
        showCanadianEducationLevel,
        showJobOfferNoc,
        showSecondLanguageScores,
        showSpouseLanguageScores,
    } = useCRSConditionals(formData);

    // --- Local State for UI Feedback ---
    const [isCalculating, setIsCalculating] = useState(false); // State for loading indicator
    const [submitError, setSubmitError] = useState(null);     // State for displaying errors
    const [saveStatus, setSaveStatus] = useState({ saving: false, error: null, success: false }); // State for save button
    const [calculatedScore, setCalculatedScore] = useState(null); // State to hold the calculated score

    // Determine available second language tests
    const getAvailableSecondLanguageTests = useCallback(() => {
        if (formData.firstLanguageTest === BAD_VALUE) return [];
        const firstTestName = testValueToNameMap[formData.firstLanguageTest];
        const isFirstEnglish = ['celpip', 'ielts', 'pte'].includes(firstTestName);
        const available = languageTestOptions.filter(opt => {
            if (opt.value === BAD_VALUE) return false;
            const secondTestName = testValueToNameMap[opt.value];
            const isSecondEnglish = ['celpip', 'ielts', 'pte'].includes(secondTestName);
            return isFirstEnglish ? !isSecondEnglish : isSecondEnglish;
        });
        available.push({ value: "NA", label: "Not applicable" });
        return available;
    }, [formData.firstLanguageTest]);

    const availableSecondLanguageTests = getAvailableSecondLanguageTests();

    // --- Submit Handler ---
    const handleSubmit = async (e) => { // Make async if needed, though calculation itself is synchronous
        e.preventDefault();
        setSubmitError(null); // Clear previous errors
        setCalculatedScore(null); // Reset score on new calculation
        setSaveStatus({ saving: false, error: null, success: false }); // Reset save status

        // --- Validation ---
        const validationError = validateCRSForm(formData, {
            showSpouseIsPRQuestion,
            showSpouseAccompanyQuestion,
            showCanadianEducationLevel,
            showJobOfferNoc,
            showSpouseFactors,
        });

        if (validationError) {
            setSubmitError(validationError); // Show validation error in the Alert
            // Optionally scroll to the error field here
            return; // Stop submission
        }

        // --- Start Calculation Process ---
        setIsCalculating(true); // Show loading indicator

        // Using setTimeout to ensure the loading state updates visually before potentially blocking calculation
        // Adjust delay as needed, or remove if calculation is extremely fast / non-blocking
        await new Promise(resolve => setTimeout(resolve, 50));

        try {
            // --- Mapping ---
            const dataForCalculation = mapFormDataToCalculationParams(formData, { showSpouseFactors });
            // console.log("Data for Official Calculation:", dataForCalculation);

            // --- Calculation ---
            const result = calculateOfficialCRS(dataForCalculation);
            setCalculatedScore(result.grandTotal); // Store the calculated score

            // Pass the detailed result up to the parent component
            if (onScoreCalculated) {
                onScoreCalculated(result);
            }
            // Clear error on success
            setSubmitError(null);

        } catch (error) {
            console.error("Error calculating CRS score:", error);
            setSubmitError("An error occurred during CRS calculation. Please check your inputs or try again later."); // Show calculation error
            if (onScoreCalculated) {
                onScoreCalculated(null); // Notify parent about the error
            }
        } finally {
            setIsCalculating(false); // Hide loading indicator regardless of success/failure
        }
    };

    const handleSaveProfile = async () => {
        if (calculatedScore === null) {
            setSaveStatus({ saving: false, error: 'No score has been calculated to save.', success: false });
            return;
        }

        setSaveStatus({ saving: true, error: null, success: false });

        try {
            // --- Data Transformation ---
            // Map the internal form data codes to human-readable labels before saving.
            const labeledProfile = mapFormDataToLabels(formData, {
                maritalStatusOptions,
                ageOptions,
                educationLevelOptions,
                canadianEducationLevelOptions,
                languageTestOptions,
                languageScoreOptions,
                canadianWorkExperienceOptions,
                foreignWorkExperienceOptions,
                yesNoOptions,
                jobOfferNocOptions
            });

            const response = await axiosInstance.post('api/timeline/crs-profile', {
                crsScore: calculatedScore,
                crsProfile: labeledProfile // Send the transformed, readable data
            });

            if (response.status === 200) {
                setSaveStatus({ saving: false, error: null, success: true });
            } else {
                throw new Error(response.data.message || 'Failed to save profile.');
            }
        } catch (error) {
            console.error("Error saving CRS profile:", error);
            const errorMessage = error.response?.data?.message || error.message || "An unknown error occurred.";
            setSaveStatus({ saving: false, error: errorMessage, success: false });
        }
    };

    // --- Render ---
    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 3, mt: 2 }}> {/* Increased gap slightly */}
            {/* Optional Name Field */}
            {/* <TextField label="Name (Optional)" variant="outlined" name="name" value={formData.name} onChange={handleChange} /> */}

            {/* Form Sections */}
            <PersonalSection
                formData={formData}
                handleChange={handleChange}
                handleFocus={handleFocus}
                showSpouseIsPRQuestion={showSpouseIsPRQuestion}
                showSpouseAccompanyQuestion={showSpouseAccompanyQuestion}
            />

            <EducationSection
                formData={formData}
                handleChange={handleChange}
                handleFocus={handleFocus}
                showCanadianEducationLevel={showCanadianEducationLevel}
            />

            <LanguageSection
                formData={formData}
                handleChange={handleChange}
                handleLanguageScoreChange={handleLanguageScoreChange}
                handleFocus={handleFocus}
                availableSecondLanguageTests={availableSecondLanguageTests}
                showSecondLanguageScores={showSecondLanguageScores}
            />

            <WorkExperienceSection
                formData={formData}
                handleChange={handleChange}
                handleFocus={handleFocus}
            />

            <AdditionalPointsSection
                formData={formData}
                handleChange={handleChange}
                handleFocus={handleFocus}
                showJobOfferNoc={showJobOfferNoc}
            />

            {/* --- Spouse Factors Section (Conditional with Collapse) --- */}
            <Collapse in={showSpouseFactors} timeout="auto" unmountOnExit>
                 {/* Add a Box or Paper inside Collapse if needed for styling/padding */}
                 <Box sx={{ mt: 2, mb: 1, /* Add padding/border if desired */ }}>
                      <Typography variant="h6" component="p" gutterBottom sx={{ mb: 2 }}>
                          Spouse/Partner Factors
                      </Typography>
                      <SpouseFactorsSection
                          formData={formData}
                          handleChange={handleChange}
                          handleLanguageScoreChange={handleLanguageScoreChange}
                          handleFocus={handleFocus}
                          showSpouseLanguageScores={showSpouseLanguageScores}
                      />
                 </Box>
            </Collapse>


            {/* Display Submission Error */}
            {submitError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {submitError}
                </Alert>
            )}

            {/* Submit Button with Loading State */}
            <Button
                className="myButton" // Keep if custom styles rely on this
                variant="contained"
                color="primary"
                type="submit"
                sx={{ mt: 3, justifySelf: 'center', px: 5, py: 1.5, minWidth: '220px' }} // Added minWidth for consistency
                // Disable button while calculating OR if language test isn't valid (B=Valid)
                disabled={isCalculating || formData.firstLanguageTestValid !== 'B'}
                startIcon={isCalculating ? <CircularProgress size={20} color="inherit" /> : null}
            >
                {isCalculating ? 'Calculating...' : 'Calculate CRS Score'}
            </Button>

            {/* Save Profile Button and Status */}
            {calculatedScore !== null && user && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleSaveProfile}
                        disabled={saveStatus.saving || saveStatus.success}
                        startIcon={saveStatus.saving ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {saveStatus.success ? 'Profile Saved!' : saveStatus.saving ? 'Saving...' : 'Save Score & Profile to Timeline'}
                    </Button>
                    {saveStatus.error && <Alert severity="error" sx={{ mt: 1, width: '100%' }}>{saveStatus.error}</Alert>}
                    {saveStatus.success && <Alert severity="success" sx={{ mt: 1, width: '100%' }}>Your CRS profile has been successfully saved to your timeline.</Alert>}
                </Box>
            )}

        </Box>
    );
}

// Export the component
export { CRSScoreForm };